import React, { FC, useState, useEffect, useCallback } from 'react'
import styles from 'styles/components/template.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { push } from 'connected-react-router'
import QRCode from 'qrcode.react'
import { BlueButton, CautionButton, GreyButton } from 'components/Button'
import { FlexRow } from 'components/Flex'
import { TextInput } from 'components/Form'
import { FormLabel } from 'components/Label'
import { Description, MainHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { openCautionModal } from 'reducks/modal/operations'
import { getPointCard } from 'reducks/pointCard/selectors'
import { fetchPointCardCode } from 'repos/pointCard/pointCardCode'
import { deletePointCardCode } from 'repos/pointCard/pointCardCode'
import { PointCardCodeType } from 'types/pointCard/pointCardCode'
import { getStoreRef } from 'reducks/auth/selectors'

type Props = {
  pointCardCodeId: string
}

const PointCardCodeDetailTemplate: FC<Props> = (props) => {
  const { pointCardCodeId } = props

  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const [pointCardCode, setPointCardCode] = useState<PointCardCodeType>()
  const [src, setSrc] = useState<string>('')
  const [link, setLink] = useState<string>('')
  const pointCard = useSelector((state: State) => getPointCard(state))

  const handleDeletePointCardCode = async () => {
    if (!pointCard || !pointCardCodeId) return
    try {
      await deletePointCardCode(pointCard.ref.id, pointCardCodeId)
      dispatch(push('/point_card/code/list'))
    } catch (error) {
      console.error(error)
    }
  }

  const handleOpenConfirmModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '削除する',
        'キャンセル',
        `一度削除したQRコードは、元に戻すことができません。\n<span style="color: red">※印刷されているQRコードも利用できなくなります。</span>`,
        handleDeletePointCardCode,
        `<p style="text-align: left">QRコードを削除します。</p>`
      )
    )
  }, [pointCard, pointCardCodeId])

  // canvasに描画したデータをa.downloadにセット（iOS Chromeだとダウンロードできない）
  const downloadQrCode = () => {
    const canvas = document.getElementById('qrcode') as HTMLCanvasElement
    if (!canvas || !pointCardCode) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png', 0.85)
    a.download = `${pointCardCode.name}_${pointCardCode.points}pt.png`
    a.click()
  }

  useEffect(() => {
    ;(async () => {
      if (!pointCard || !pointCardCodeId) return
      const pointCardCode = await fetchPointCardCode(pointCard.ref.id, pointCardCodeId)
      setPointCardCode(pointCardCode)
    })()
  }, [pointCard, pointCardCodeId])

  // canvasに描画したデータをimgタグのsrcにセット
  useEffect(() => {
    const canvas = document.getElementById('qrcode') as HTMLCanvasElement
    if (!canvas) return
    setSrc(canvas.toDataURL('image/png', 0.85))
  }, [link])

  useEffect(() => {
    if (!pointCardCode) return
    setLink(`sacri://point-card/create?code=${pointCardCode.code}&store_id=${storeRef.id}`)
  }, [pointCardCode])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <MainHeadline headline={pointCardCode ? pointCardCode.name : ''} />
      <Spacer size={20} />
      <Description description={'QRコードを保存して、店舗などで利用してください。'} />
      <Spacer size={20} />
      <FormLabel label={'付与するポイント'} required={false} />
      <Spacer size={20} />
      <TextInput
        disabled={true}
        required={false}
        type={'point'}
        size={'small'}
        value={pointCardCode ? `${pointCardCode.points}pt` : ''}
      />
      <Spacer size={20} />
      <FlexRow justifyContent={'center'}>
        <QRCode id={'qrcode'} size={343} value={link} style={{ display: 'none' }} />
        <img src={src} alt={pointCard ? pointCard.name : ''} width={343} height={343} />
      </FlexRow>
      <Spacer size={20} />
      <BlueButton label={'画像を保存する'} onClick={downloadQrCode} size={'medium'} type={'main'} />
      <Spacer size={40} />
      <FlexRow justifyContent={'center'}>
        <BlueButton
          label={'編集する'}
          onClick={() => dispatch(push(`/point_card/code/${pointCardCodeId}/edit`))}
          size={'xsmall'}
          type={'outlined'}
        />
        <Spacer size={20} />
        <CautionButton label={'削除する'} onClick={handleOpenConfirmModal} size={'xsmall'} type={'outlined'} />
      </FlexRow>
      <Spacer size={24} />
      <GreyButton
        label={'QRコードに戻る'}
        onClick={() => dispatch(push('/point_card/code/list'))}
        size={'medium'}
        type={'outlined'}
      />
      <Spacer size={40} />
    </div>
  )
}

export default PointCardCodeDetailTemplate
